import { RubricCategory } from '../hooks/useRubric';

/**
 * Redistributes weights among categories to ensure they sum to 100
 * @param categories - Array of rubric categories
 * @param modifiedCategoryId - Optional ID of category that was manually modified
 * @returns Array of categories with adjusted weights
 */
export const redistributeWeights = (
  categories: RubricCategory[],
  modifiedCategoryId?: string
): RubricCategory[] => {
  const totalWeight = categories.reduce((sum, cat) => sum + cat.weight, 0);
  
  // If weights already sum to 100, no adjustment needed
  if (totalWeight === 100) return categories;
  
  // If no categories, return empty array
  if (categories.length === 0) return categories;
  
  // If one category was manually set, distribute remainder among others
  if (modifiedCategoryId) {
    const modifiedCategory = categories.find(c => c.id === modifiedCategoryId);
    if (!modifiedCategory) return categories;
    
    const otherCategories = categories.filter(c => c.id !== modifiedCategoryId);
    
    // If this is the only category, set it to 100
    if (otherCategories.length === 0) {
      return categories.map(cat => ({
        ...cat,
        weight: 100
      }));
    }
    
    const remainingWeight = 100 - modifiedCategory.weight;
    const otherTotalWeight = otherCategories.reduce((sum, c) => sum + c.weight, 0);
    
    // If other categories have no weight, distribute equally
    if (otherTotalWeight === 0) {
      const equalWeight = remainingWeight / otherCategories.length;
      return categories.map(cat => {
        if (cat.id === modifiedCategoryId) return cat;
        return { ...cat, weight: Math.round(equalWeight) };
      });
    }
    
    // Otherwise, distribute proportionally
    return categories.map(cat => {
      if (cat.id === modifiedCategoryId) return cat;
      
      const proportion = cat.weight / otherTotalWeight;
      return {
        ...cat,
        weight: Math.round(remainingWeight * proportion)
      };
    });
  }
  
  // If no specific category was modified, adjust all proportionally
  if (totalWeight === 0) {
    // If all weights are 0, distribute equally
    const equalWeight = 100 / categories.length;
    return categories.map(cat => ({
      ...cat,
      weight: Math.round(equalWeight)
    }));
  }
  
  // Proportionally adjust all categories
  let adjustedCategories = categories.map(cat => ({
    ...cat,
    weight: Math.round((cat.weight / totalWeight) * 100)
  }));
  
  // Handle rounding errors - ensure total is exactly 100
  const adjustedTotal = adjustedCategories.reduce((sum, cat) => sum + cat.weight, 0);
  if (adjustedTotal !== 100) {
    // Add/subtract the difference to/from the largest weight
    const sortedByWeight = [...adjustedCategories].sort((a, b) => b.weight - a.weight);
    sortedByWeight[0].weight += (100 - adjustedTotal);
    
    // Update the category in the original array
    adjustedCategories = adjustedCategories.map(cat => 
      cat.id === sortedByWeight[0].id ? sortedByWeight[0] : cat
    );
  }
  
  return adjustedCategories;
};

/**
 * Validates that category weights sum to 100
 * @param categories - Array of rubric categories
 * @returns true if weights sum to 100, false otherwise
 */
export const validateWeights = (categories: RubricCategory[]): boolean => {
  const total = categories.reduce((sum, cat) => sum + cat.weight, 0);
  return total === 100;
};

/**
 * Suggests a weight for a new category based on existing categories
 * @param existingCategories - Array of existing categories
 * @returns Suggested weight for the new category
 */
export const suggestNewCategoryWeight = (existingCategories: RubricCategory[]): number => {
  if (existingCategories.length === 0) return 100;
  
  // Calculate what equal distribution would be with one more category
  const equalWeight = Math.round(100 / (existingCategories.length + 1));
  
  // If current weights are already unequal, use a smaller default
  const currentWeights = existingCategories.map(c => c.weight);
  const minWeight = Math.min(...currentWeights);
  const maxWeight = Math.max(...currentWeights);
  
  if (maxWeight - minWeight > 20) {
    // Weights are very unequal, suggest a modest weight
    return Math.min(20, equalWeight);
  }
  
  return equalWeight;
};